import { c as _c } from "react/compiler-runtime";
import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { BottomSheetBody, BottomSheetHeader, BottomSheetHeaderTitle, Flex, Separator, useToasts } from "@opensea/ui-kit";
import { useCallback } from "react";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants";
import { useAddress } from "../../../polychain/hooks/useAddress";
import { FlowActions, FlowState } from "../../../stores/flowStore";
import { useAllActionsCompleted } from "../../hooks/useAllActionsCompleted";
import { useSetItemImageMap, useSetItemImageMapFragment } from "../../hooks/useSetItemImageMap";
import { useSetQuoteItems, useSetQuoteItemsFragment } from "../../hooks/useSetQuoteItems";
import { useSetTotalUsdPrice, useSetTotalUsdPriceFragment } from "../../hooks/useSetTotalUsdPrice";
import { CheckoutPaymentSection } from "../../PaymentSection/CheckoutPaymentSection";
import { CollectionSweepToast } from "../../ToastHandler/CollectionSweepToast/CollectionSweepToast";
import { CollectionSweepFlow_quoteFragment } from "./CollectionSweepFlow.fragment";
import { CollectionSweepFlowSkeleton } from "./CollectionSweepFlowSkeleton";
import { CollectionSweepReceipt } from "./components/CollectionSweepReceipt";
import { CollectionSweepFlowStatus } from "./components/CollectionSweepStatus";
import { CollectionSweepTimeline } from "./components/CollectionSweepTimeline/CollectionSweepTimeline";
const query = graphql(`
    query CollectionSweepQuoteFlowQuery(
      $collectionSlug: String!
      $maxAmountOfItems: Int!
      $maxPricePerItem: BigDecimal!
      $buyer: Address
    ) {
      sweepCollectionQuote(
        collectionSlug: $collectionSlug
        maxAmountOfItems: $maxAmountOfItems
        maxPricePerItem: $maxPricePerItem
        buyer: $buyer
      ) {
        ...CollectionSweepFlow_quote

        quote {
          ... on CollectionSweepQuoteSuccess {
            itemQuotes {
              ...useSetQuoteItems
              ...useSetItemImageMap
              ...useSetTotalUsdPrice
            }
          }
        }
      }
    }
  `, [CollectionSweepFlow_quoteFragment, useSetQuoteItemsFragment, useSetItemImageMapFragment, useSetTotalUsdPriceFragment]);
function getSuccessfulItemQuotes(data) {
  if (!data?.sweepCollectionQuote.quote || data.sweepCollectionQuote.quote.__typename !== "CollectionSweepQuoteSuccess") {
    return undefined;
  }
  return data.sweepCollectionQuote.quote.itemQuotes;
}
export function CollectionSweepFlow(t0) {
  const $ = _c(25);
  const {
    collectionSlug,
    maxAmountOfItems,
    maxPricePerItem
  } = t0;
  const t = useTranslations("wallet.CollectionSweepFlow");
  const address = useAddress();
  const {
    addCustomToast
  } = useToasts();
  let t1;
  if ($[0] !== maxPricePerItem) {
    t1 = maxPricePerItem.toString();
    $[0] = maxPricePerItem;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== address || $[3] !== collectionSlug || $[4] !== maxAmountOfItems || $[5] !== t1) {
    t2 = {
      query,
      variables: {
        collectionSlug,
        maxAmountOfItems,
        maxPricePerItem: t1,
        buyer: address
      },
      context: NETWORK_ONLY_OPERATION_CONTEXT
    };
    $[2] = address;
    $[3] = collectionSlug;
    $[4] = maxAmountOfItems;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const [t3] = useQuery(t2);
  const {
    data,
    error,
    fetching
  } = t3;
  let itemQuotes;
  let t4;
  if ($[7] !== data || $[8] !== maxAmountOfItems) {
    itemQuotes = getSuccessfulItemQuotes(data);
    t4 = itemQuotes?.slice(0, maxAmountOfItems);
    $[7] = data;
    $[8] = maxAmountOfItems;
    $[9] = itemQuotes;
    $[10] = t4;
  } else {
    itemQuotes = $[9];
    t4 = $[10];
  }
  const bestItemQuotes = t4;
  useSetQuoteItems(bestItemQuotes);
  useSetItemImageMap(itemQuotes);
  useSetTotalUsdPrice(bestItemQuotes);
  const {
    flow
  } = useSnapshot(FlowState);
  const mode = flow?.mode;
  const [allActionsCompleted] = useAllActionsCompleted();
  let t5;
  if ($[11] !== addCustomToast || $[12] !== mode) {
    t5 = (_response, receiptTransactions) => {
      if (mode === "minimized" && receiptTransactions.length > 0) {
        const {
          itemQuotes: itemQuotes_0,
          itemImageMap,
          flow: flow_0
        } = FlowState;
        addCustomToast(() => <CollectionSweepToast flow={flow_0} itemImageMap={itemImageMap} itemQuotes={itemQuotes_0} transactionIdentifiers={receiptTransactions} />, {
          id: "collection-sweep-toast",
          duration: 15000
        });
        FlowActions.endFlow();
      }
    };
    $[11] = addCustomToast;
    $[12] = mode;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleAllActionsCompleted = t5;
  if (fetching) {
    let t6;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <CollectionSweepFlowSkeleton count={1} />;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  if (error || !data?.sweepCollectionQuote) {
    throw new Error("There was a problem with collection sweep");
  }
  let t6;
  if ($[15] !== data.sweepCollectionQuote) {
    t6 = readFragment(CollectionSweepFlow_quoteFragment, data.sweepCollectionQuote);
    $[15] = data.sweepCollectionQuote;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const quote = t6;
  let t7;
  if ($[17] !== allActionsCompleted || $[18] !== collectionSlug || $[19] !== handleAllActionsCompleted || $[20] !== maxAmountOfItems || $[21] !== maxPricePerItem || $[22] !== quote || $[23] !== t) {
    t7 = <>{allActionsCompleted ? <BottomSheetBody><CollectionSweepFlowStatus collectionImageUrl={quote.collection.imageUrl} /></BottomSheetBody> : <><BottomSheetHeader><BottomSheetHeaderTitle>{t("title")}</BottomSheetHeaderTitle></BottomSheetHeader><BottomSheetBody><Flex className="size-full flex-col gap-6 md:flex-row md:gap-10"><CollectionSweepReceipt maxAmountOfItems={maxAmountOfItems} quote={quote} /><Separator className="hidden h-auto md:block" orientation="vertical" /><CheckoutPaymentSection cardSupported={false} currencySelector={null} timeline={<CollectionSweepTimeline collectionSlug={collectionSlug} maxAmountOfItems={maxAmountOfItems} maxPricePerItem={maxPricePerItem} onAllActionsCompleted={handleAllActionsCompleted} quote={quote} />} /></Flex></BottomSheetBody></>}</>;
    $[17] = allActionsCompleted;
    $[18] = collectionSlug;
    $[19] = handleAllActionsCompleted;
    $[20] = maxAmountOfItems;
    $[21] = maxPricePerItem;
    $[22] = quote;
    $[23] = t;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}